import { useState } from "react";

import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { usePostHog } from "posthog-js/react";

import { PersonalInformationDisclaimer } from "components/PersonalInformationDisclaimer";
import { ClientAppEvent } from "constants/index";
import {
  useDisclosure,
  useRoleContext,
  useShipmentPageContext,
  useWriterComponentState,
} from "hooks";

interface Props {
  questionId: number;
}

export function SubmitAnswerMenu(props: Props) {
  const { questionId } = props;
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [answer, setAnswer] = useState<string>("");
  const { onRefresh } = useShipmentPageContext();
  const { role } = useRoleContext();
  const posthog = usePostHog();

  const { write, resetState, loading, error } = useWriterComponentState({
    writeConfig: {
      method: "post",
      url: `/shipmentQAs/${questionId}/answers`,
      body: { answer },
    },
    errorMessage: "Failed to submit answer",
    errorMessageKey: "error",
  });

  const postAnswer = () => {
    write(() => {
      posthog?.capture(ClientAppEvent.SHIPMENT_QUESTION_ANSWERED, {
        role,
        question_id: questionId,
        // Answers written outside the app are captured by the backend listener
        // under this same name, so both halves carry `source` and one breakdown
        // splits the series
        // (docs/engineering/design/qa_product_analytics_listener_design.md §8).
        source: "CUSTOMER_APP" satisfies SourceType,
      });
      onClose();
      if (onRefresh) {
        onRefresh();
      }
    });
  };

  const onModalClose = () => {
    resetState();
    onClose();
  };

  const getPostAnswerDialog = () => (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Confirm Response</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Please confirm that you wish to send the following response:
        </DialogContentText>
        <Box mt="1rem" mb="1rem" textAlign="center">
          <em>{answer}</em>
        </Box>
        <PersonalInformationDisclaimer />
        <Stack mt="1rem">
          <Typography color="red" align="center">
            {error}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onModalClose}>Cancel</Button>
        <Button onClick={postAnswer} autoFocus disabled={loading}>
          {loading ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <div>
      {getPostAnswerDialog()}
      <Stack direction="column" sx={{ mt: "1rem" }}>
        <Typography variant="body1" sx={{ mt: ".5rem", mb: ".5rem" }}>
          Respond to the carrier&apos;s question in the text box below.
          Responses are public.
        </Typography>
        <Stack direction="row" mt="1rem" justifyContent="center" spacing={2}>
          <TextField
            fullWidth
            required
            label="Answer"
            onChange={(e) => setAnswer(e.target.value)}
          />
          <Button
            disabled={answer.length < 1}
            variant="contained"
            sx={{ pl: "2rem", pr: "2rem" }}
            onClick={onOpen}
          >
            Send
          </Button>
        </Stack>
      </Stack>
    </div>
  );
}
